import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { motion } from 'framer-motion';
import { usePoints, useAuth } from '../App';
import notify from '../utils/notify';
import { syncGoogleFitSteps, getTodaySteps } from '../utils/fitnessSync';
import ImageUploadPicker from '../components/shared/ImageUploadPicker';
import { ArrowLeft, CalendarDays, Clock, MapPin, Star, MoreVertical, Flag, Pencil, Navigation, Video } from 'lucide-react';
import { buildMapUrls } from '../utils/mapLinks';
import { formatActionDates, displayLocation } from '../utils/actionDates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical prefill for the "share this log" text and tags.
// Single source of truth used by BOTH the success-screen Share button
// (navigates to /create-post with a draft) AND the inline "Also share
// to WE Feed" toggle. Do not duplicate this logic elsewhere.
const buildLogSharePrefill = ({ challenge, logValue, value, selectedMetricIdx }) => {
  const shareTitle = challenge?.title || 'my action';
  const isSteps = challenge?.challenge_type === 'steps';
  const metricsList = Array.isArray(challenge?.metrics) ? challenge.metrics : [];
  const hasMetrics = metricsList.length > 0;
  let content;
  if (hasMetrics) {
    const m = metricsList[Math.min(selectedMetricIdx || 0, metricsList.length - 1)] || metricsList[0];
    const numStr = String(logValue ?? '').trim();
    content = numStr
      ? `Just logged ${numStr} ${m.metric_id || ''} for "${shareTitle}"! `.replace(/\s+/g, ' ')
      : `Just logged progress on "${shareTitle}"! `;
  } else if (isSteps) {
    content = `Just logged ${value || 0} minutes for "${shareTitle}"!`;
  } else {
    content = `Just completed "${shareTitle}"! `;
  }
  return { content, tags: ['MADAction', 'Progress'] };
};

// ─────────────────────────────────────────────────────────────────────
// Metric-aware Log Progress — shared building blocks.
//
// Both entry points that let a user log against a metric-wired challenge
// (the challenge-detail-page modal in `ChallengeDetailPage` and the
// ME-page /log-activity form in `LogActivityPage`) MUST flow through
// the pieces below. Do not re-implement the metric input or the
// impact_entries call in a caller — extend these helpers instead.
// ─────────────────────────────────────────────────────────────────────
const METRIC_INPUT_COLORS = {
  primary: '#5DBFAD',
  text: '#1F2937',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  white: '#FFFFFF',
};

/**
 * Controlled metric-input UI:
 *   - pill selector when `metrics.length > 1` (never silent-defaults),
 *   - labeled numeric input using `metrics[selectedIdx].prompt`.
 * Renders null if `metrics` is empty/invalid.
 */
const MetricInput = ({
  metrics,
  selectedIdx,
  onSelectIdx,
  value,
  onChange,
  disabled = false,
  inputTestId = 'log-value-input',
}) => {
  if (!Array.isArray(metrics) || metrics.length === 0) return null;
  const idx = Math.min(Math.max(0, selectedIdx || 0), metrics.length - 1);
  const selected = metrics[idx] || metrics[0];
  return (
    <>
      {metrics.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: METRIC_INPUT_COLORS.textSecondary, marginBottom: 6 }}>
            What are you logging?
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {metrics.map((m, i) => {
              const active = i === idx;
              return (
                <button
                  key={m.metric_id || i}
                  type="button"
                  data-testid={`log-metric-choice-${m.metric_id || i}`}
                  onClick={() => onSelectIdx(i)}
                  disabled={disabled}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 999,
                    border: `1px solid ${active ? METRIC_INPUT_COLORS.primary : METRIC_INPUT_COLORS.border}`,
                    background: active ? METRIC_INPUT_COLORS.primary : METRIC_INPUT_COLORS.white,
                    color: active ? METRIC_INPUT_COLORS.white : METRIC_INPUT_COLORS.text,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {m.metric_id}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: METRIC_INPUT_COLORS.text, marginBottom: 6 }}>
        {selected?.prompt || 'Enter your progress value'}
      </label>
      <input
        data-testid={inputTestId}
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 5"
        disabled={disabled}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${METRIC_INPUT_COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
      />
    </>
  );
};

/**
 * Parse + validate a user-typed metric value.
 * Returns { ok: true, parsed } on success, or { ok: false, error } on failure.
 */
const parseMetricValue = (raw) => {
  const s = String(raw ?? '').trim();
  const parsed = Number(s);
  if (!s || !Number.isFinite(parsed) || parsed <= 0) {
    return { ok: false, error: 'Please enter a number greater than 0.' };
  }
  return { ok: true, parsed };
};

/**
 * POST /impact/entries wrapper. Never throws. Returns:
 *   - null on 2xx
 *   - a warning string on any 4xx/5xx (typically the 409 daily-duplicate)
 * so callers can surface a toast without duplicating error-normalisation.
 */
const submitImpactEntry = async ({ orgId, userId, actionId, metricId, rawValue }) => {
  try {
    await axios.post(`${API}/impact/entries`, {
      org_id: orgId,
      user_id: userId,
      action_id: actionId,
      metric_id: metricId,
      raw_value: rawValue,
      submitted_by: userId,
    });
    return null;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const detailMsg = typeof detail === 'string'
      ? detail
      : detail?.error || 'Impact entry could not be recorded.';
    return `Progress saved, but today's impact number wasn't recorded: ${detailMsg}`;
  }
};

/**
 * StepsSyncPanel — shared UI for triggering a Google Fit sync from any
 * page that needs it (currently LogActivityPage; StepTrackingPage has
 * its own richer layout but calls the same underlying helper).
 *
 * Renders a "Sync Steps" button + inline status text. On success, calls
 * `onSynced(stepsFound)` so the parent can gate its manual input the
 * way today's `/challenges/log` backend guard expects (Fit rows for
 * today block manual entries).
 */
const StepsSyncPanel = ({ onSynced, disabled = false }) => {
  const navigate = useNavigate();
  const [syncing, setSyncing] = React.useState(false);
  const [status, setStatus] = React.useState(null); // {type, msg, steps?, action?}

  const handleClick = async () => {
    if (syncing || disabled) return;
    setSyncing(true);
    setStatus(null);
    const result = await syncGoogleFitSteps();
    if (result.ok) {
      setStatus({ type: 'success', msg: `Synced ${result.steps.toLocaleString()} steps from Google Fit.`, steps: result.steps });
      if (typeof onSynced === 'function') onSynced(result.steps);
    } else if (result.needsConnect) {
      setStatus({
        type: 'error',
        msg: 'Google Fit is not connected.',
        action: { label: 'Connect on Steps page', to: '/steps' },
      });
    } else {
      setStatus({ type: 'error', msg: result.error || 'Sync failed. Please try again.' });
    }
    setSyncing(false);
  };

  const COLORS = METRIC_INPUT_COLORS;
  return (
    <div style={{ marginBottom: 16, padding: '14px 14px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 10, background: COLORS.white }}>
      <button
        type="button"
        data-testid="log-activity-sync-steps-btn"
        onClick={handleClick}
        disabled={syncing || disabled}
        style={{
          width: '100%',
          padding: '10px 14px',
          background: disabled ? '#E5E7EB' : COLORS.primary,
          color: disabled ? '#9CA3AF' : COLORS.white,
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: (syncing || disabled) ? 'not-allowed' : 'pointer',
        }}
      >
        {syncing ? 'Syncing…' : 'Sync Steps from Google Fit'}
      </button>
      {status && (
        <div
          data-testid="log-activity-sync-status"
          style={{
            marginTop: 10,
            fontSize: 12,
            color: status.type === 'success' ? COLORS.primary : '#DC2626',
          }}
        >
          {status.msg}
          {status.action && (
            <>
              {' '}
              <button
                type="button"
                data-testid="log-activity-sync-connect-link"
                onClick={() => navigate(status.action.to)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#DC2626', fontSize: 12, fontWeight: 700,
                  textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                {status.action.label}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// GitHub-style Activity Calendar Component
const ActivityHeatmap = ({ userId }) => {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    fetchActivities();
  }, [userId]);

  const fetchActivities = async () => {
    try {
      // Fetch all user's challenge activities
      const response = await axios.get(`${API}/activities/my?limit=90`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const generateCalendarData = () => {
    const weeks = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 83); // 12 weeks back
    
    // Create a map of dates with activity counts
    const activityMap = new Map();
    activities.forEach(activity => {
      const date = new Date(activity.date).toDateString();
      const count = activityMap.get(date) || 0;
      activityMap.set(date, count + 1);
    });
    
    // Generate 12 weeks
    for (let week = 0; week < 12; week++) {
      const weekData = [];
      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (week * 7) + day);
        
        const dateStr = currentDate.toDateString();
        const count = activityMap.get(dateStr) || 0;
        
        weekData.push({
          date: currentDate,
          count: count
        });
      }
      weeks.push(weekData);
    }
    
    return weeks;
  };

  const weeks = generateCalendarData();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getCellColor = (count) => {
    if (count === 0) return '#EBEDF0';
    if (count === 1) return '#C6E48B';
    if (count === 2) return '#7BC96F';
    if (count >= 3) return '#239A3B';
    return '#196127';
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Action Activity</h3>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 3, minWidth: 'fit-content' }}>
          {/* Day labels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingRight: 8 }}>
            <div style={{ height: 12 }} />
            {weekDays.map((day, i) => (
              <div 
                key={i} 
                style={{ 
                  fontSize: 9, 
                  color: 'var(--text-medium)', 
                  height: 12, 
                  lineHeight: '12px',
                  display: i % 2 === 1 ? 'block' : 'none'
                }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Month label */}
              <div style={{ height: 12, fontSize: 9, color: 'var(--text-medium)', fontWeight: 600 }}>
                {week[0].date.getDate() <= 7 ? week[0].date.toLocaleDateString('en', { month: 'short' }) : ''}
              </div>
              
              {week.map((dayData, dayIndex) => (
                <div
                  key={dayIndex}
                  title={`${dayData.date.toLocaleDateString()} - ${dayData.count} ${dayData.count === 1 ? 'activity' : 'activities'}`}
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: getCellColor(dayData.count),
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.3)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, fontSize: 11, color: 'var(--text-medium)' }}>
        <span>Less</span>
        <div style={{ display: 'flex', gap: 3 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#EBEDF0', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#C6E48B', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#7BC96F', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#239A3B', borderRadius: 2 }} />
          <div style={{ width: 12, height: 12, backgroundColor: '#196127', borderRadius: 2 }} />
        </div>
        <span>More</span>
      </div>
    </div>
  );
};


// Challenge-specific Activity Calendar Component
const ChallengeActivityCalendar = ({ challengeId, userId, userProgress, challenge }) => {
  const [activities, setActivities] = useState([]);
  const [streak, setStreak] = useState(0);
  const [totalActivities, setTotalActivities] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    fetchActivities();
    fetchLeaderboard();
  }, [challengeId, userId]);

  const fetchActivities = async () => {
    try {
      // Fetch activities for this specific challenge
      const response = await axios.get(`${API}/activities/my?limit=200`);
      // Filter to only this challenge
      const challengeActivities = response.data.filter(act => act.challenge_id === challengeId);
      setActivities(challengeActivities);
      setTotalActivities(challengeActivities.length);
      
      // Calculate points earned
      const points = challengeActivities.reduce((sum, act) => sum + (act.social_score_earned || 0), 0);
      setPointsEarned(points);
      
      // Calculate streak (consecutive days)
      calculateStreak(challengeActivities);
    } catch (error) {
      console.error('Failed to fetch challenge activities:', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeId}/leaderboard`);
      const userEntry = response.data.find(entry => entry.user_id === userId);
      if (userEntry) {
        setUserRank(userEntry.rank);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const calculateStreak = (acts) => {
    if (acts.length === 0) {
      setStreak(0);
      return;
    }

    // Sort by date descending
    const sortedActs = [...acts].sort((a, b) => new Date(b.date) - new Date(a.date));
    
    let currentStreak = 1;
    for (let i = 1; i < sortedActs.length; i++) {
      const prevDate = new Date(sortedActs[i - 1].date);
      const currDate = new Date(sortedActs[i].date);
      const diffDays = Math.floor((prevDate - currDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
    setStreak(currentStreak);
  };

  const generateCalendarData = () => {
    const weeks = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 83); // 12 weeks back
    
    // Create a map of dates with activity data
    const activityMap = new Map();
    activities.forEach(activity => {
      const date = new Date(activity.date).toDateString();
      activityMap.set(date, activity);
    });
    
    // Generate 12 weeks
    for (let week = 0; week < 12; week++) {
      const weekData = [];
      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (week * 7) + day);
        
        const dateStr = currentDate.toDateString();
        const activity = activityMap.get(dateStr);
        
        weekData.push({
          date: currentDate,
          hasActivity: !!activity,
          value: activity?.value || 0
        });
      }
      weeks.push(weekData);
    }
    
    return weeks;
  };

  const weeks = generateCalendarData();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getCellColor = (dayData) => {
    if (!dayData.hasActivity) return '#EBEDF0';
    return '#10B981'; // Green for participated days
  };

  const getProgressPercentage = () => {
    if (!challenge || challenge.target_value === 0) return 0;
    return Math.min(100, Math.round((userProgress / challenge.target_value) * 100));
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Calendar */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Your Participation Calendar</h3>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 3, minWidth: 'fit-content' }}>
            {/* Day labels */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingRight: 8 }}>
              <div style={{ height: 14 }} />
              {weekDays.map((day, i) => (
                <div 
                  key={i} 
                  style={{ 
                    fontSize: 10, 
                    color: 'var(--text-medium)', 
                    height: 14, 
                    lineHeight: '14px',
                    display: i % 2 === 1 ? 'block' : 'none'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Month label */}
                <div style={{ height: 14, fontSize: 10, color: 'var(--text-medium)', fontWeight: 600 }}>
                  {week[0].date.getDate() <= 7 ? week[0].date.toLocaleDateString('en', { month: 'short' }) : ''}
                </div>
                
                {week.map((dayData, dayIndex) => (
                  <div
                    key={dayIndex}
                    title={`${dayData.date.toLocaleDateString()}${dayData.hasActivity ? ` - ${dayData.value} logged` : ' - No activity'}`}
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: getCellColor(dayData),
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.3)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, fontSize: 11, color: 'var(--text-medium)' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ width: 14, height: 14, backgroundColor: '#EBEDF0', borderRadius: 2 }} />
            <span>No activity</span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ width: 14, height: 14, backgroundColor: '#10B981', borderRadius: 2 }} />
            <span>Participated</span>
          </div>
        </div>
      </div>

      {/* Motivation Metrics - Grid Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {/* Streak Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', 
          borderRadius: 12, 
          padding: 16,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 32, marginBottom: 4 }}>{streak}🔥</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>Day Streak</div>
        </div>

        {/* Points Earned Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)', 
          borderRadius: 12, 
          padding: 16,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 28, marginBottom: 4, fontWeight: 700, color: '#1E40AF' }}>
            {pointsEarned.toLocaleString()}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E3A8A' }}>Points Earned</div>
        </div>

        {/* Rank Card */}
        {userRank && (
          <div style={{ 
            background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)', 
            borderRadius: 12, 
            padding: 16,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 32, marginBottom: 4 }}>
              {userRank === 1 ? '🥇' : userRank === 2 ? '🥈' : userRank === 3 ? '🥉' : `#${userRank}`}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#065F46' }}>Your Rank</div>
          </div>
        )}
      </div>

      {/* Second Row of Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
        {/* Activities Count Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)', 
          borderRadius: 12, 
          padding: 16,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 28, marginBottom: 4, fontWeight: 700, color: '#0C4A6E' }}>
            {totalActivities}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E' }}>Activities Logged</div>
        </div>

        {/* Progress Bar */}
        <div style={{ 
          background: 'white', 
          borderRadius: 12, 
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 8, textAlign: 'center' }}>
            Completion Progress
          </div>
          <div style={{ 
            width: '100%', 
            height: 8, 
            background: '#EBEDF0', 
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: 6
          }}>
            <div style={{ 
              width: `${getProgressPercentage()}%`, 
              height: '100%', 
              background: 'var(--gradient-primary)',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', color: 'var(--primary-teal)' }}>
            {getProgressPercentage()}%
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionMoreMenu = ({ onEdit, onFlag, flagged, colors }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [open]);
  const item = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 14px', background: 'none', border: 'none', fontSize: 14, color: colors.text, cursor: 'pointer', textAlign: 'left' };
  const pick = (fn) => () => { setOpen(false); fn(); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="action-more-menu-btn"
        aria-label="More options"
        aria-expanded={open}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10 }}
      >
        <MoreVertical size={22} />
      </button>
      {open && (
        <div data-testid="action-more-menu" role="menu" style={{ position: 'absolute', right: 0, top: 46, minWidth: 200, background: colors.white, border: `1px solid ${colors.border}`, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' }}>
          <button role="menuitem" onClick={pick(onEdit)} data-testid="hr-edit-challenge-btn" style={item}>
            <Pencil size={16} color={colors.textSecondary} /> Edit
          </button>
          <button
            role="menuitem"
            onClick={pick(onFlag)}
            data-testid={flagged ? 'hr-unflag-detail' : 'hr-flag-detail'}
            title={flagged ? 'Unflag Action' : 'Flag Action'}
            style={{ ...item, borderTop: `1px solid ${colors.border}` }}
          >
            <Flag size={16} color={colors.textMuted} /> {flagged ? 'Flagged · Remove flag' : 'Report a problem'}
          </button>
        </div>
      )}
    </div>
  );
};


export const ChallengeDetailPage = ({ BottomNav, TopHeader, ProgressPlant, useAuth }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshPoints } = usePoints();
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userProgress, setUserProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [logStatus, setLogStatus] = useState({ can_log: true, button_text: 'Log Progress' });
  const [myStats, setMyStats] = useState(null);
  // Map of metric_id → { label, unit } from /impact/metric-definitions.
  // Used to label per-user totals on the challenge leaderboard with the
  // real unit (e.g. "kg" for plastic, "litres" for water) instead of a
  // hardcoded "min". Populated once per detail-page mount; a metric
  // definition rarely changes, so a fresh fetch each mount is cheap.
  const [metricDefsMap, setMetricDefsMap] = useState({});
  const challengeId = window.location.pathname.split('/').pop();

  // Check if user is HR Admin
  const isHRAdmin = user?.role === 'HR_ADMIN';

  // HR Flag MAD Action
  const handleFlagChallenge = async (reason) => {
    try {
      await axios.post(`${API}/hr/challenges/${challengeId}/flag${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      alert('Action flagged for review');
      fetchData(); // Refresh to show updated status
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to flag Action');
    }
  };

  // HR Unflag MAD Action
  const handleUnflagChallenge = async () => {
    try {
      await axios.post(`${API}/hr/challenges/${challengeId}/unflag`);
      alert('Action unflagged');
      fetchData(); // Refresh to show updated status
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to unflag Action');
    }
  };

  // Theme colors - clean, professional
  const COLORS = {
    primary: '#5DBFAD',
    secondary: '#73C8B9',
    softBg: 'rgba(180, 225, 216, 0.1)',
    accent: '#FBC98A',
    highlight: '#F8AD7B',
    text: '#1F2937',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    border: '#E5E7EB',
    white: '#FFFFFF',
    surface: '#FAFAFA'
  };

  useEffect(() => {
    fetchData();
  }, [challengeId]);

  const fetchData = async () => {
    try {
      const [challengeRes, leaderboardRes, logStatusRes, metricDefsRes] = await Promise.all([
        axios.get(`${API}/challenges/${challengeId}`),
        axios.get(`${API}/challenges/${challengeId}/leaderboard`),
        axios.get(`${API}/challenges/${challengeId}/log-status`).catch(() => ({ data: { can_log: true, button_text: 'Log Progress' } })),
        axios.get(`${API}/impact/metric-definitions`).catch(() => ({ data: [] })),
      ]);
      setChallenge(challengeRes.data);
      setLeaderboard(leaderboardRes.data);
      setLogStatus(logStatusRes.data);
      // Build the metric_id → definition map used by the leaderboard
      // unit label (see the `unitLabel` derivation near the leaderboard
      // JSX). Empty on network failure — the fallback branch handles it.
      const defsMap = {};
      (metricDefsRes.data || []).forEach(d => { if (d?.metric_id) defsMap[d.metric_id] = d; });
      setMetricDefsMap(defsMap);

      const userEntry = leaderboardRes.data.find(entry => entry.user_id === user.id);
      setUserProgress(userEntry?.total || 0);

      // Fetch user's own stats if joined
      if (challengeRes.data.participants?.includes(user.id)) {
        try {
          const statsRes = await axios.get(`${API}/challenges/${challengeId}/my-stats`);
          setMyStats(statsRes.data);
        } catch { setMyStats(null); }
      }
    } catch (error) {
      console.error('Failed to fetch challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysLeft = () => {
    if (!challenge) return 0;
    const endDate = new Date(challenge.end_date);
    const now = new Date();
    return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
  };

  const getProgressPercentage = () => {
    if (!challenge || challenge.target_value === 0) return 0;
    return Math.min(100, Math.round((challenge.total_progress / challenge.target_value) * 100));
  };

  const getUserProgressPercentage = () => {
    if (!challenge) return 0;
    // Use the same target fallback chain as the Overall Progress text so
    // the bar and the "X of Y" label always agree. Never hardcode 1.
    const target = challenge.target_value || challenge.frequency_value || challenge.duration_days || 0;
    if (!target) return 0;
    return Math.min(100, Math.round((userProgress / target) * 100));
  };

  const handleJoinFromDetail = async () => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/join`);
      await fetchData();
      notify.success('Successfully joined! 🎉');
    } catch (error) {
      console.error('Failed to join challenge:', error);
      notify.error(error?.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeaveChallenge = async () => {
    setShowLeaveConfirm(false);
    try {
      await axios.delete(`${API}/challenges/${challengeId}/leave`);
      // Stay on the same page — refetch so the button reverts to Join, the
      // Overall Progress card hides, and stats clear. Refresh the points pill
      // in the top header so the deducted stars show immediately.
      refreshPoints();
      await fetchData();
      notify.success('You have left this action.');
    } catch (error) {
      console.error('Failed to leave challenge:', error);
      notify.error(error?.response?.data?.detail || 'Failed to leave this action.');
    }
  };

  const handleDeleteChallenge = async () => {
    setShowDeleteConfirm(false);
    try {
      await axios.delete(`${API}/challenges/${challengeId}`);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to delete challenge:', error);
      alert(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const isUserInChallenge = () => {
    return challenge?.participants?.includes(user?.id);
  };

  const isCreator = () => {
    return challenge?.created_by === user?.id;
  };

  // Get user's rank
  const getUserRank = () => {
    const idx = leaderboard.findIndex(entry => entry.user_id === user?.id);
    return idx >= 0 ? idx + 1 : '-';
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!challenge?.end_date) return 0;
    const endDate = new Date(challenge.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };
  const daysRemaining = getDaysRemaining();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!challenge) {
    return <div className="loading">Challenge not found</div>;
  }

  const isEnded = (!!challenge?.end_date && new Date(challenge.end_date) < new Date())
    || (!isUserInChallenge() && daysRemaining <= 0);
  const ctaBase = {
    width: '100%', minHeight: 48, padding: '12px 16px', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word',
  };
  const locationText = displayLocation(challenge);
  const starsReward = challenge.reward_points ?? challenge.reward_stars ?? challenge.stars_reward;
  const quickFacts = [
    challenge.start_date && { key: 'date', icon: CalendarDays, text: formatActionDates(challenge) },
    challenge.start_time && { key: 'time', icon: Clock, text: `${challenge.start_time}${challenge.end_time ? ` – ${challenge.end_time}` : ''}` },
    locationText && { key: 'location', icon: challenge.is_online ? Video : MapPin, text: locationText },
    starsReward > 0 && { key: 'stars', icon: Star, text: `${starsReward} stars` },
  ].filter(Boolean);

  return (
    <div className="page-container" style={{ background: COLORS.surface }}>
      {/* Top bar: back + more menu */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 8px',
        background: COLORS.white,
        borderBottom: `1px solid ${COLORS.border}`
      }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="action-detail-back-btn"
          aria-label="Back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.text, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10 }}
        >
          <ArrowLeft size={22} />
        </button>
        {isHRAdmin && (
          <ActionMoreMenu
            onEdit={() => navigate(`/challenges/${challenge.id}/edit`, { state: { fromDetailId: challenge.id } })}
            flagged={!!challenge.flagged_by_hr}
            onFlag={() => {
              if (challenge.flagged_by_hr) {
                handleUnflagChallenge();
              } else {
                const reason = window.prompt('Reason for flagging this Action:');
                if (reason !== null) handleFlagChallenge(reason);
              }
            }}
            colors={COLORS}
          />
        )}
      </div>

      {/* Flagged Warning Banner - shown for HR when viewing flagged content */}
      {challenge.flagged_by_hr && isHRAdmin && (
        <div style={{
          background: '#FEE2E2',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
            This Action is under HR review
          </span>
        </div>
      )}

      <div className="content-wrapper action-detail-content" style={{ padding: '16px' }}>
        {/* Title + quick facts */}
        <h1
          data-testid="action-detail-title"
          title={challenge.title}
          style={{
            fontSize: 22, fontWeight: 700, color: COLORS.text, lineHeight: 1.4, margin: '4px 0 12px',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            overflowWrap: 'anywhere', paddingBottom: 2,
          }}
        >
          {challenge.title}
        </h1>
        {quickFacts.length > 0 && (
          <div data-testid="action-quick-facts" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {quickFacts.map(({ key, icon: Icon, text }) => (
              <span
                key={key}
                data-testid={`action-chip-${key}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                  padding: '6px 10px', background: COLORS.white, border: `1px solid ${COLORS.border}`,
                  borderRadius: 999, fontSize: 12, fontWeight: 500, color: COLORS.text, lineHeight: 1.4,
                }}
              >
                <Icon size={14} color={COLORS.primary} style={{ flexShrink: 0 }} />
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{text}</span>
              </span>
            ))}
          </div>
        )}

        {/* Main action: sticky bottom bar on phones, inline button on desktop */}
        <div className="action-cta-bar" data-testid="action-cta-bar">
          {isEnded ? (
            <div
              data-testid="action-ended-label"
              style={{ ...ctaBase, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' }}
            >
              This action has ended
            </div>
          ) : isUserInChallenge() ? (
            <button
              onClick={() => logStatus.can_log && navigate(`/log-activity?challenge=${challenge.id}`, { state: { fromDetailId: challenge.id } })}
              disabled={!logStatus.can_log}
              data-testid="log-progress-btn"
              style={{ ...ctaBase, background: logStatus.can_log ? COLORS.primary : '#cccccc', color: COLORS.white, cursor: logStatus.can_log ? 'pointer' : 'not-allowed' }}
            >
              {logStatus.button_text}
            </button>
          ) : (
            <button
              onClick={handleJoinFromDetail}
              data-testid="join-action-btn"
              style={{ ...ctaBase, background: COLORS.primary, color: COLORS.white, cursor: 'pointer' }}
            >
              Join
            </button>
          )}
        </div>

        {/* When & Where Section */}
        <div style={{ 
          marginBottom: 20, 
          background: COLORS.white,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '14px 16px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: COLORS.text 
            }}>
              When & Where
            </div>
            {challenge.schedule_auto_generated && (
              <div data-testid="suggested-schedule-badge" style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#0F766E',
                padding: '3px 10px',
                background: '#E0F2F1',
                borderRadius: 999,
                whiteSpace: 'nowrap'
              }}>
                Suggested schedule
              </div>
            )}
          </div>
          <div style={{ padding: '14px 16px' }}>
            {/* Location (hidden when there is no real location, link or online flag) */}
            {(locationText || challenge.map_link) && (
            <div data-testid="action-location-row" style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 12,
              marginBottom: 14
            }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: COLORS.softBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div data-testid="action-location-text" style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>
                  {locationText || 'Location on map'}
                </div>
                {challenge.venue_details && !challenge.venue_details_auto_generated && (
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.4 }}>
                    {challenge.venue_details}
                  </div>
                )}
                {challenge.is_online && !isEnded && (
                  challenge.meeting_link ? (
                    <a href={challenge.meeting_link} target="_blank" rel="noopener noreferrer" data-testid="action-join-online"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '8px 14px', minHeight: 36, borderRadius: 999, background: COLORS.primary, color: COLORS.white, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                      <Video size={15} /> Join online
                    </a>
                  ) : (
                    <div data-testid="action-join-link-pending" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '6px 12px', borderRadius: 999, background: '#F1F5F9', color: '#475569', fontSize: 12, fontWeight: 600 }}>
                      <Video size={14} /> Link will be shared soon
                    </div>
                  )
                )}
                {!challenge.is_online && (() => {
                  const urls = buildMapUrls(challenge.location_auto_generated ? '' : challenge.location, challenge.map_link);
                  if (!urls) return null;
                  const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', minHeight: 32, borderRadius: 999, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: '#0F766E', fontSize: 12, fontWeight: 600, textDecoration: 'none' };
                  return (
                    <div data-testid="action-map-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      <a href={urls.open} target="_blank" rel="noopener noreferrer" data-testid="action-open-in-maps" style={btn}>
                        <MapPin size={14} /> Open in Maps
                      </a>
                      <a href={urls.directions} target="_blank" rel="noopener noreferrer" data-testid="action-directions" style={btn}>
                        <Navigation size={14} /> Directions
                      </a>
                    </div>
                  );
                })()}
              </div>
            </div>
            )}

            {/* Date Range */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 12,
              marginBottom: 14
            }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: COLORS.softBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>
                  <span data-testid="action-date-text">{formatActionDates(challenge)}</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {challenge.challenge_type === 'specific-time' ? 'One-time' : `${challenge.duration_days} days`} • {daysRemaining} days remaining
                </div>
              </div>
            </div>

            {/* Time */}
            {(challenge.start_time || challenge.recurring_schedule) && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 12
              }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: COLORS.softBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  {challenge.start_time && (
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>
                      {challenge.start_time}{challenge.end_time ? ` - ${challenge.end_time}` : ''}
                    </div>
                  )}
                  {challenge.recurring_schedule && (
                    <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      {challenge.recurring_schedule}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* What You'll Do - Instructions Section */}
        <div style={{ 
          marginBottom: 20, 
          background: COLORS.white,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '14px 16px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: COLORS.text 
            }}>
              What you'll do
            </div>
            {challenge.instructions_auto_generated && (
              <div style={{
                fontSize: 10,
                fontWeight: 500,
                color: COLORS.textMuted,
                padding: '3px 8px',
                background: COLORS.softBg,
                borderRadius: 4
              }}>
                Suggested steps
              </div>
            )}
          </div>
          <div style={{ padding: '12px 16px' }}>
            {(challenge.instructions || []).map((instruction, idx) => (
              <div 
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: idx < (challenge.instructions || []).length - 1 
                    ? `1px solid ${COLORS.border}` 
                    : 'none'
                }}
              >
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: COLORS.softBg,
                  border: `1.5px solid ${COLORS.primary}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: COLORS.primary,
                  flexShrink: 0
                }}>
                  {idx + 1}
                </div>
                <div style={{
                  fontSize: 13,
                  color: COLORS.textSecondary,
                  lineHeight: 1.5,
                  paddingTop: 2
                }}>
                  {instruction}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Overall Progress Card — only visible to users who have joined this
            MAD action. Shows their personal progress (X logs of Y required),
            not the org-wide aggregate (which made 2 people × 1 each look like "2/1"). */}
        {/* {isUserInChallenge() && (
        <div style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: '20px',
          marginBottom: 16,
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{
            display: 'inline-block',
            padding: '4px 10px',
            background: COLORS.softBg,
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.primary,
            marginBottom: 16,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {getDaysLeft()} days remaining
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>
            Overall Progress
          </div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 12 }}>
            {(() => {
              // Y = the action's actual goal pulled from the DB
              // (target_value, then frequency_value, then duration_days).
              // We never default to 1 — if no goal is set, we just show "X logs".
              const target = challenge.target_value || challenge.frequency_value || challenge.duration_days || null;
              const done = userProgress?.toLocaleString() || 0;
              const unit = challenge.challenge_type === 'steps' ? 'steps' : 'logs';
              return target
                ? `${done} of ${target.toLocaleString()} ${unit} required`
                : `${done} ${unit}`;
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${getUserProgressPercentage()}%`, height: '100%', background: COLORS.secondary, borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.primary, minWidth: 40, textAlign: 'right' }}>
              {getUserProgressPercentage()}%
            </span>
          </div>
        </div>
        )} */}

        {/* Sync-steps prompt — only shown when (a) the user has joined, (b) the
            action involves walking/running/steps, and (c) the user has NOT
            already connected Google Fit. Linking back to More → Step Tracking. */}
        {isUserInChallenge() && (() => {
          const txt = `${challenge.title || ''} ${challenge.description || ''} ${challenge.category || ''}`.toLowerCase();
          const isStepLike = challenge.challenge_type === 'steps' || /\b(walk|run|step)/i.test(txt);
          const alreadyConnected = !!user?.google_fit_connected;
          if (!isStepLike || alreadyConnected) return null;
          return (
            <div
              data-testid="sync-steps-prompt"
              style={{
                background: '#F0F9FF',
                border: '1px solid #BAE6FD',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 16,
                fontSize: 13,
                color: '#0C4A6E',
                lineHeight: 1.5,
              }}
            >
              Sync your steps automatically — go to{' '}
              <span
                data-testid="sync-steps-link"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/steps')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/steps'); } }}
                style={{ color: '#0284C7', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Step Tracking
              </span>
            </div>
          );
        })()}

        {/* Stats Cards */}
        {isUserInChallenge() && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
          <div style={{ background: COLORS.white, borderRadius: 10, padding: '16px 8px', textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.primary, marginBottom: 4 }}>{myStats?.stars || 0}</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Stars</div>
          </div>
          <div style={{ background: COLORS.white, borderRadius: 10, padding: '16px 8px', textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.primary, marginBottom: 4 }}>{myStats?.streak || 0}</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Streak</div>
          </div>
          <div style={{ background: COLORS.white, borderRadius: 10, padding: '16px 8px', textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.primary, marginBottom: 4 }}>#{myStats?.rank || getUserRank()}</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Rank</div>
          </div>
          <div style={{ background: COLORS.white, borderRadius: 10, padding: '16px 8px', textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.primary, marginBottom: 4 }}>{myStats?.days_logged || 0}</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Days</div>
          </div>
        </div>
        )}

        {/* Leave Action — destructive outlined button, only shown to users who
            have joined. Placed directly below the progress / stats so it's
            findable but never confused with the primary "Log Progress" CTA. */}
        {isUserInChallenge() && (
          <div style={{ marginBottom: 20 }}>
            <button
              data-testid="leave-action-btn"
              onClick={() => setShowLeaveConfirm(true)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: COLORS.white,
                color: '#DC2626',
                border: '1.5px solid #FCA5A5',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Leave Action
            </button>
          </div>
        )}

        {/* Activity Log - Minimal Design */}
        {isUserInChallenge() && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 12 
          }}>
            <h3 style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: COLORS.text,
              margin: 0
            }}>
              Activity Log
            </h3>
          </div>

          <div style={{
            background: COLORS.white,
            borderRadius: 10,
            padding: '16px',
            border: `1px solid ${COLORS.border}`
          }}>
            {/* Minimal Progress Indicator */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4, 
              marginBottom: 12 
            }}>
              {[1, 2, 3, 4, 5, 6, 7].map(day => (
                <div 
                  key={day} 
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: day <= (myStats?.week_days_logged || 0) ? COLORS.secondary : COLORS.border
                  }}
                />
              ))}
            </div>
            
            {/* Text-based progress */}
            <div style={{ 
              fontSize: 13, 
              color: COLORS.textSecondary,
              lineHeight: 1.5
            }}>
              <span style={{ fontWeight: 600, color: COLORS.text }}>{myStats?.week_days_logged || 0} of 7 days</span> logged this week
            </div>
          </div>
        </div>
        )}

        {/* Leaderboard - Flat List Style */}
        {(() => {
          // Derive the unit label for per-user totals on the leaderboard.
          // Metric-wired challenges (Nagpur set + any future) carry
          // `metrics[0].metric_id`, whose real unit lives in
          // `metric_definitions` (fetched into metricDefsMap above).
          // Legacy branches: `challenge_type === 'steps'` still tracks
          // step counts; everything else without a metric is a binary
          // completion challenge, so `completions` is the honest label.
          const firstMetricId = Array.isArray(challenge?.metrics) && challenge.metrics.length > 0
            ? challenge.metrics[0]?.metric_id
            : null;
          const unitLabel = firstMetricId
            ? (metricDefsMap[firstMetricId]?.unit || 'units')
            : (challenge?.challenge_type === 'steps' ? 'steps' : 'completions');
          return (
        <div style={{ marginBottom: 20 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 12 
          }}>
            <h3 style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: COLORS.text,
              margin: 0
            }}>
              Leaderboard
            </h3>
          </div>

          <div style={{
            background: COLORS.white,
            borderRadius: 10,
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden'
          }}>
            {leaderboard.slice(0, 5).map((entry, index) => (
              <div
                key={entry.user_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: index === 0 ? 'rgba(251, 201, 138, 0.15)' : 'transparent',
                  borderBottom: index < Math.min(leaderboard.length, 5) - 1 ? `1px solid ${COLORS.border}` : 'none'
                }}
              >
                {/* Rank - Text only */}
                <div style={{
                  width: 24,
                  fontSize: 14,
                  fontWeight: 600,
                  color: index === 0 ? COLORS.highlight : COLORS.textMuted,
                  textAlign: 'center'
                }}>
                  {index + 1}
                </div>

                {/* Avatar */}
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: COLORS.softBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLORS.primary,
                  fontSize: 14,
                  fontWeight: 600
                }}>
                  {entry.username?.[0]?.toUpperCase() || 'U'}
                </div>

                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: 500, 
                    color: COLORS.text 
                  }}>
                    {entry.username || 'User'}
                    {entry.user_id === user?.id && (
                      <span style={{ 
                        marginLeft: 6, 
                        fontSize: 11, 
                        color: COLORS.primary 
                      }}>
                        (You)
                      </span>
                    )}
                  </div>
                  {index === 0 && (
                    <div style={{ 
                      fontSize: 11, 
                      color: COLORS.textMuted, 
                      marginTop: 1 
                    }}>
                      Current leader
                    </div>
                  )}
                </div>

                {/* Score */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: COLORS.text 
                  }}>
                    {entry.total?.toLocaleString() || 0}
                  </div>
                  <div style={{ 
                    fontSize: 10, 
                    color: COLORS.textMuted,
                    textTransform: 'uppercase'
                  }}
                    data-testid="leaderboard-unit-label"
                  >
                    {unitLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
          );
        })()}

        {/* Bottom Action — Delete Challenge (creator only). The Leave button
            is now in its own prominent block right after the progress stats. */}
        {isUserInChallenge() && isCreator() && (
          <div style={{ paddingBottom: 80, textAlign: 'center' }}>
            <button
              data-testid="delete-challenge-btn"
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#DC2626',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '8px 12px'
              }}
            >
              Delete this action
            </button>
          </div>
        )}
        {!isUserInChallenge() && isCreator() && (
          <div style={{ paddingBottom: 80, textAlign: 'center' }}>
            <button
              data-testid="delete-challenge-btn"
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#DC2626',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '12px'
              }}
            >
              Delete this action
            </button>
          </div>
        )}
      </div>

      {/* Leave Confirmation Modal - Clean */}
      {showLeaveConfirm && (
        <div onClick={() => setShowLeaveConfirm(false)} style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: COLORS.white, borderRadius: 12, padding: 24,
            maxWidth: 340, width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: COLORS.text }}>
              Leave this action?
            </h3>
            <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>
              Are you sure you want to leave this action? Your progress will be lost and your stars for this action will be deducted.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button data-testid="leave-cancel-btn" onClick={() => setShowLeaveConfirm(false)}
                style={{ flex: 1, padding: '12px', background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: 13, color: COLORS.text }}>
                Cancel
              </button>
              <button data-testid="leave-confirm-btn" onClick={handleLeaveChallenge}
                style={{ flex: 1, padding: '12px', background: '#DC2626', color: COLORS.white,
                  border: 'none', borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div onClick={() => setShowDeleteConfirm(false)} style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: COLORS.white, borderRadius: 12, padding: 24,
            maxWidth: 340, width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: COLORS.text }}>
              Delete this Action?
            </h3>
            <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>
              This will permanently delete this action and all related progress logs. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowDeleteConfirm(false)}
                style={{ flex: 1, padding: '12px', background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: 13, color: COLORS.text }}>
                Cancel
              </button>
              <button data-testid="confirm-delete-challenge-btn" onClick={handleDeleteChallenge}
                style={{ flex: 1, padding: '12px', background: '#DC2626', color: COLORS.white,
                  border: 'none', borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Log Progress Modal removed — the modal was unreachable
          (`setShowLogModal(true)` was never called anywhere) and its
          state has been superseded by `LogActivityPage`. */}
    </div>
  );
};

export const LogActivityPage = ({ TopHeader, BottomNav }) => {
  const logLocation = useLocation();
  const logNavigate = useNavigate();
  // After a successful log: pop back to the details page it was opened from, else replace the form in history.
  const goToDetailAfterLog = (id) => {
    if (id && logLocation.state?.fromDetailId === id) logNavigate(-1);
    else logNavigate(`/challenges/${id}`, { replace: true });
  };
  const navigate = useNavigate();
  const { refreshPoints } = usePoints();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const preselectedChallengeId = searchParams.get('challenge');

  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [activityType, setActivityType] = useState('steps');
  const [value, setValue] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false); // For non-steps challenges
  // Metric-aware state. Only used when `selectedChallenge.metrics` is a
  // non-empty array — same shape/semantics as the challenge-detail modal.
  const [logValue, setLogValue] = useState('');
  const [selectedMetricIdx, setSelectedMetricIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [logStatus, setLogStatus] = useState({ can_log: true, button_text: 'Log Progress' });

  // ── Optional "Also share to WE Feed" section ─────────────────────
  // Off by default — matches the safe-by-default share_to_feed
  // decision. The `POST /challenges/log` call is UNCHANGED (still
  // `share_to_feed: false`). When the toggle is on AND at least one
  // of content/media is set, we ALSO fire `POST /posts` — same
  // endpoint the CreatePostPage uses. No duplicate share pipeline.
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [shareMediaUrl, setShareMediaUrl] = useState('');
  const [sharePreviewUrl, setSharePreviewUrl] = useState('');
  const [shareUploading, setShareUploading] = useState(false);
  // Set true ONLY when the inline POST /posts call actually succeeds.
  // Used by the success screen to hide the redundant Share button when
  // the user has already shared inline.
  const [sharedInline, setSharedInline] = useState(false);
  const SHARE_TAG_SUGGESTIONS = ['#Wellness', '#Climate', '#Community', '#Career'];
  const insertShareTag = (tag) => {
    setShareContent(prev => (prev.endsWith(' ') || prev === '') ? `${prev}${tag} ` : `${prev} ${tag} `);
  };

  // Clean theme colors
  const COLORS = {
    primary: '#5DBFAD',
    secondary: '#73C8B9',
    softBg: 'rgba(180, 225, 216, 0.1)',
    accent: '#FBC98A',
    highlight: '#F8AD7B',
    text: '#1F2937',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    border: '#E5E7EB',
    white: '#FFFFFF'
  };

  // Flat line icons
  const StepsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="12" y1="6" x2="16" y2="6" />
      <line x1="12" y1="10" x2="16" y2="10" />
    </svg>
  );

  const MeditationIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );

  const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  useEffect(() => {
    fetchChallenges();
  }, []);

  // When the selected challenge is the metric-wired steps challenge,
  // pre-fill the manual input with today's Google-Fit-synced step count
  // (if any). Purely a convenience seed — the input remains editable so
  // users can override the number before submitting, and there's no
  // backend gate that blocks the submission either.
  useEffect(() => {
    const metrics = Array.isArray(selectedChallenge?.metrics) ? selectedChallenge.metrics : [];
    const firstMetric = metrics[0];
    if (firstMetric?.metric_id !== 'steps') return;
    let cancelled = false;
    (async () => {
      const r = await getTodaySteps();
      if (cancelled) return;
      if (r.ok && r.source === 'google_fit' && r.steps > 0) {
        // Only pre-fill if the user hasn't already typed something. Do
        // not clobber in-progress input on refetch.
        setLogValue((prev) => (String(prev).trim() ? prev : String(r.steps)));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedChallenge]);


  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/challenges/my`);
      const myChallenges = response.data;
      setChallenges(myChallenges);

      let picked = null;
      if (preselectedChallengeId) {
        const preselected = myChallenges.find(c => c.id === preselectedChallengeId);
        if (preselected) {
          picked = preselected;
          setSelectedChallenge(preselected);
          setActivityType(preselected.challenge_type);
        }
      } else if (myChallenges.length > 0) {
        picked = myChallenges[0];
        setSelectedChallenge(myChallenges[0]);
        setActivityType(myChallenges[0].challenge_type);
      }
      if (picked) {
        try {
          const lsRes = await axios.get(`${API}/challenges/${picked.id}/log-status`);
          setLogStatus(lsRes.data);
        } catch { setLogStatus({ can_log: true, button_text: 'Log Progress' }); }
      }
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const isStepsChallenge = selectedChallenge?.challenge_type === 'steps';

  const calculatePoints = () => {
    if (!selectedChallenge) return 0;
    const metrics = Array.isArray(selectedChallenge?.metrics) ? selectedChallenge.metrics : [];
    const hasMetrics = metrics.length > 0;

    // Preview mirrors challenges.py:619-633 exactly: flat base=10 for every
    // activity type; steps branch removed. Only condition for base>0 is
    // "did the user provide a non-zero log value for this submission".
    const numeric = hasMetrics ? Number(String(logValue).trim()) : (isCompleted ? 1 : 0);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    const basePoints = 10;

    const modeMultiplier = { individual: 1, team: 3, company: 3, community: 5 };
    const multiplier = modeMultiplier[selectedChallenge.mode] || 1;

    return Math.max(1, Math.floor(basePoints * multiplier));
  };

  // Handle image selection
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChallenge) return;

    // Metric-wired challenges take the user-typed number; everything else
    // stays on the legacy binary "value = isCompleted ? 1 : 0" path.
    const metricsList = Array.isArray(selectedChallenge?.metrics) ? selectedChallenge.metrics : [];
    const hasMetrics = metricsList.length > 0;

    let logValueForBackend;    // int sent to /challenges/log
    let rawImpactValue = null; // exact float sent to /impact/entries (metric branch only)
    let chosenMetric = null;

    if (hasMetrics) {
      const parseResult = parseMetricValue(logValue);
      if (!parseResult.ok) {
        notify.error(parseResult.error);
        return;
      }
      chosenMetric = metricsList[Math.min(selectedMetricIdx, metricsList.length - 1)] || metricsList[0];
      if (!chosenMetric?.metric_id) {
        notify.error('This action has no valid metric configured. Please contact HR.');
        return;
      }
      rawImpactValue = parseResult.parsed;
      // Backend `ActivityLog.value` / `ChallengeActivity.value` /
      // `Challenge.total_progress` are all float now, so send the exact
      // typed number to both writes — no rounding, both ledgers agree.
      logValueForBackend = rawImpactValue;
    } else {
      // Unchanged legacy behavior — do NOT modify this branch.
      const effectiveValue = isStepsChallenge ? value : (isCompleted ? 1 : 0);
      if (effectiveValue <= 0) return;
      logValueForBackend = isStepsChallenge ? parseInt(value) : 1;
    }

    setSubmitting(true);
    try {
      // This page is now log-only — `share_to_feed: false` on every
      // request, no image upload, no share_* payload fields. Users who
      // want to share to the WE feed do so from the success screen
      // (which navigates them to /create-post with a prefilled draft).
      const response = await axios.post(`${API}/challenges/${selectedChallenge.id}/log`, {
        activity_type: activityType,
        value: logValueForBackend,
        date: new Date().toISOString(),
        share_to_feed: false,
      });

      // For metric-wired challenges only: also write to impact_entries.
      // Uses the same shared helper as the challenge-detail modal — a
      // failure here (typically the 409 daily-duplicate) does NOT roll
      // back the progress log above; it surfaces as a warning toast.
      let impactWarning = null;
      if (hasMetrics && user?.id && user?.org_id) {
        impactWarning = await submitImpactEntry({
          orgId: user.org_id,
          userId: user.id,
          actionId: selectedChallenge.id,
          metricId: chosenMetric.metric_id,
          rawValue: rawImpactValue,
        });
      }

      setPointsEarned(response.data.social_score_earned);
      setSuccess(true);
      refreshPoints();
      if (impactWarning) notify.warning(impactWarning);

      // Optional: ALSO create a WE-feed post if the user opted in.
      // Uses the same `POST /api/posts` endpoint CreatePostPage uses —
      // this is a second CALLER, not a second implementation. A failure
      // here surfaces as a warning toast but does NOT roll back the log.
      if (shareEnabled && (shareContent.trim() || shareMediaUrl)) {
        try {
          await axios.post(`${API}/posts`, {
            content: shareContent.trim(),
            tags: (shareContent.match(/#\w+/g) || []).map(t => t.slice(1)),
            media_url: shareMediaUrl || null,
            challenge_id: selectedChallenge.id,
          });
        } catch (shareErr) {
          console.error('Failed to share to WE feed:', shareErr);
          notify.warning('Progress logged, but sharing to the WE Feed failed.');
        }
      }
      // No auto-navigate — the user stays on the success screen and
      // explicitly chooses Share or Done. Previously we redirected to
      // the challenge detail after 1.5s, which raced against a user
      // trying to tap Share.
    } catch (error) {
      console.error('Failed to log activity:', error);
      // Backend metric-guard returns `{error: "..."}` at the top level
      // (JSONResponse, no `detail` wrapper). Legacy paths still use
      // `{detail: "..."}` (string) or occasionally `{detail: {...}}`.
      // Normalise all three into a single readable string.
      const data = error?.response?.data;
      const msg = data?.error
        || (typeof data?.detail === 'string' ? data.detail : null)
        || data?.detail?.error
        || (typeof data?.detail === 'object' && data.detail !== null ? JSON.stringify(data.detail) : null)
        || 'Failed to log activity. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ background: COLORS.white }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          color: COLORS.textSecondary,
          fontSize: 14
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (challenges.length === 0) {
    return (
      <div className="page-container" style={{ background: COLORS.white }}>
        {/* Minimal Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: 4,
              marginRight: 12,
              color: COLORS.text
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>Log Activity</span>
        </div>
        
        <div style={{ 
          padding: '60px 24px', 
          textAlign: 'center' 
        }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 500, 
            color: COLORS.text, 
            marginBottom: 8 
          }}>
            No Active Actions
          </div>
          <div style={{ 
            fontSize: 13, 
            color: COLORS.textSecondary, 
            marginBottom: 24 
          }}>
            Join an action first to log your activities.
          </div>
          <button 
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: COLORS.primary,
              color: COLORS.white,
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Browse Actions
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    // Success screen — user picks Share (opens /create-post with a
    // prefilled draft) or Done (goes back to the challenge). No auto-
    // navigate; this replaces the old 1.5s redirect that raced any
    // Share tap.
    // Uses the canonical buildLogSharePrefill helper — same source as
    // the inline "Also share to WE Feed" toggle, so both text bodies
    // stay in sync for the same log.
    const { content: prefillContent, tags: prefillTags } = buildLogSharePrefill({
      challenge: selectedChallenge, logValue, value, selectedMetricIdx,
    });

    return (
      <div className="page-container" style={{ background: COLORS.white }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          padding: 40
        }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            background: COLORS.softBg,
            border: `2px solid ${COLORS.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
            color: COLORS.primary
          }}>
            <CheckIcon />
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.primary, marginBottom: 8 }}>
            +{pointsEarned} stars
          </div>
          <div style={{ fontSize: 14, color: COLORS.textSecondary, marginBottom: 28 }}>
            Activity logged successfully
          </div>
          {!sharedInline && (
            <button
              type="button"
              data-testid="log-success-share-btn"
              onClick={() => navigate('/create-post', { replace: true, state: { prefill: { content: prefillContent, tags: prefillTags } } })}
              style={{
                width: '100%',
                maxWidth: 320,
                padding: '14px 20px',
                background: COLORS.primary,
                color: COLORS.white,
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 12,
              }}
            >
              Share to WE Feed
            </button>
          )}
          <button
            type="button"
            data-testid="log-success-done-btn"
            onClick={() => goToDetailAfterLog(selectedChallenge?.id || '')}
            style={{
              width: '100%',
              maxWidth: 320,
              padding: '14px 20px',
              background: COLORS.white,
              color: COLORS.textSecondary,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Submit gating: metric-wired paths require a typed number > 0.
  // Legacy paths keep their original gates untouched.
  const metricsForGate = Array.isArray(selectedChallenge?.metrics) ? selectedChallenge.metrics : [];
  const hasMetricsForGate = metricsForGate.length > 0;
  const canSubmit = logStatus.can_log && (
    hasMetricsForGate
      ? (String(logValue).trim().length > 0 && Number(logValue) > 0)
      : (isStepsChallenge ? value > 0 : isCompleted)
  );

  return (
    <div className="page-container" style={{ background: COLORS.white, minHeight: '100vh' }}>
      {/* Minimal Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.white
      }}>
        <button 
          onClick={() => navigate(-1)} 
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer',
            padding: 4,
            marginRight: 12,
            color: COLORS.text
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <span style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>Log Activity</span>
      </div>

      <div style={{ padding: '20px', maxWidth: 480, margin: '0 auto' }}>
        {/* Activity Type Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: 0, 
          marginBottom: 24,
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          {/* <button
            type="button"
            onClick={() => {
              if (selectedChallenge?.challenge_type !== 'meditation') {
                setActivityType('steps');
              }
            }}
            disabled={selectedChallenge?.challenge_type === 'meditation'}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activityType === 'steps' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              color: activityType === 'steps' ? COLORS.primary : COLORS.textMuted,
              fontSize: 14,
              fontWeight: 500,
              cursor: selectedChallenge?.challenge_type === 'meditation' ? 'not-allowed' : 'pointer',
              opacity: selectedChallenge?.challenge_type === 'meditation' ? 0.5 : 1,
              marginBottom: -1
            }}
          >
            <StepsIcon />
            Steps
          </button>
          <button
            type="button"
            onClick={() => {
              if (selectedChallenge?.challenge_type !== 'steps') {
                setActivityType('meditation');
              }
            }}
            disabled={selectedChallenge?.challenge_type === 'steps'}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activityType === 'meditation' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              color: activityType === 'meditation' ? COLORS.primary : COLORS.textMuted,
              fontSize: 14,
              fontWeight: 500,
              cursor: selectedChallenge?.challenge_type === 'steps' ? 'not-allowed' : 'pointer',
              opacity: selectedChallenge?.challenge_type === 'steps' ? 0.5 : 1,
              marginBottom: -1
            }}
          >
            <MeditationIcon />
            Others
          </button> */}
        </div>

        {/* Challenge Selection Dropdown */}
        {challenges.length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'block', 
              fontSize: 13, 
              fontWeight: 500, 
              color: COLORS.textSecondary, 
              marginBottom: 8 
            }}>
              Select Challenge
            </label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select
                value={selectedChallenge?.id || ''}
                onChange={(e) => {
                  const challenge = challenges.find(c => c.id === e.target.value);
                  setSelectedChallenge(challenge);
                  setActivityType(challenge.challenge_type);
                  setValue(0);
                  setIsCompleted(false);
                  setLogValue('');
                  setSelectedMetricIdx(0);
                  axios.get(`${API}/challenges/${challenge.id}/log-status`)
                    .then(r => setLogStatus(r.data))
                    .catch(() => setLogStatus({ can_log: true, button_text: 'Log Progress' }));
                }}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  fontSize: 14,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  background: COLORS.white,
                  color: COLORS.text,
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {challenges.map(challenge => (
                  <option key={challenge.id} value={challenge.id}>
                    {challenge.title}
                  </option>
                ))}
              </select>
              {selectedChallenge && (
                <button
                  type="button"
                  onClick={() => navigate(`/challenges/${selectedChallenge.id}`)}
                  data-testid="view-challenge-details"
                  style={{
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: COLORS.primary,
                    background: COLORS.softBg,
                    border: `1px solid ${COLORS.primary}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Details
                </button>
              )}
            </div>
          </div>
        )}

        {/* Single Challenge Display */}
        {challenges.length === 1 && selectedChallenge && (
          <div style={{
            padding: '14px 16px',
            background: COLORS.softBg,
            borderRadius: 10,
            marginBottom: 20,
            border: `1px solid ${COLORS.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>
                {selectedChallenge.title}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>
                {selectedChallenge.user_progress?.toLocaleString() || 0} / {selectedChallenge.target_value?.toLocaleString()} ({selectedChallenge.progress_percentage || 0}%)
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/challenges/${selectedChallenge.id}`)}
              data-testid="view-challenge-details-single"
              style={{
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.primary,
                background: COLORS.white,
                border: `1px solid ${COLORS.primary}`,
                borderRadius: 6,
                cursor: 'pointer'
              }}
            >
              Details
            </button>
          </div>
        )}

        {/* Conditional Input:
              1) metric-wired challenge → shared MetricInput (numeric + prompt)
              2) legacy steps challenge  → minutes input
              3) everything else         → "Mark as completed today" checkbox */}
        {(() => {
          const metrics = Array.isArray(selectedChallenge?.metrics) ? selectedChallenge.metrics : [];
          const hasMetrics = metrics.length > 0;

          if (hasMetrics) {
            const firstMetric = metrics[0];
            const isStepsMetric = firstMetric?.metric_id === 'steps';
            return (
              <div style={{ marginBottom: 24 }}>
                {/* Steps-specific sync panel — shown only for the metric-
                    wired steps challenge. Successful sync pre-fills the
                    manual input with the synced count; the input stays
                    editable, and the backend accepts whatever number the
                    user submits (no separate Fit guard). */}
                {isStepsMetric && (
                  <StepsSyncPanel
                    onSynced={(steps) => {
                      // Pre-fill only if the user hasn't typed something
                      // already — preserve any in-progress override.
                      setLogValue((prev) => (String(prev).trim() ? prev : String(steps)));
                    }}
                  />
                )}
                <div style={{ padding: '20px 20px 4px', border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
                  <MetricInput
                    metrics={metrics}
                    selectedIdx={selectedMetricIdx}
                    onSelectIdx={(i) => { setSelectedMetricIdx(i); setLogValue(''); }}
                    value={logValue}
                    onChange={setLogValue}
                    disabled={submitting}
                    inputTestId="log-activity-value-input"
                  />
                </div>
              </div>
            );
          }

          return isStepsChallenge ? (
          /* Minutes Input for Steps Challenges */
          <div style={{ marginBottom: 24 }}>
            <div style={{
              padding: '24px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              textAlign: 'center'
            }}>
              <input
                type="number"
                value={value || ''}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  fontSize: 40,
                  fontWeight: 600,
                  textAlign: 'center',
                  border: 'none',
                  outline: 'none',
                  color: COLORS.text,
                  background: 'transparent'
                }}
              />
              <div style={{ 
                fontSize: 13, 
                color: COLORS.textMuted, 
                marginTop: 4 
              }}>
                minutes
              </div>
            </div>

            {/* Quick Add Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              marginTop: 12,
              justifyContent: 'center'
            }}>
              {[5, 10, 15].map(mins => (
                <button
                  key={mins}
                  type="button"
                  onClick={() => setValue(prev => parseInt(prev || 0) + mins)}
                  style={{
                    padding: '10px 20px',
                    background: COLORS.white,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    color: COLORS.textSecondary,
                    cursor: 'pointer'
                  }}
                >
                  +{mins}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Completion Toggle for Non-Steps Challenges */
          <div style={{ marginBottom: 24 }}>
            <div
              onClick={() => setIsCompleted(!isCompleted)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '16px 18px',
                border: `1px solid ${isCompleted ? COLORS.primary : COLORS.border}`,
                borderRadius: 10,
                cursor: 'pointer',
                background: isCompleted ? COLORS.softBg : COLORS.white
              }}
            >
              <div style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: `2px solid ${isCompleted ? COLORS.primary : COLORS.border}`,
                background: isCompleted ? COLORS.primary : COLORS.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.white,
                flexShrink: 0
              }}>
                {isCompleted && <CheckIcon />}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>
                  Mark as completed today
                </div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
                  Log one completion for this action
                </div>
              </div>
            </div>
          </div>
        );
        })()}

        {/* Points Preview Card */}
        <div style={{
          padding: '16px 18px',
          background: COLORS.softBg,
          borderRadius: 10,
          marginBottom: 20
        }}>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 4 }}>
            You'll earn
          </div>
          <div style={{ 
            fontSize: 24, 
            fontWeight: 600, 
            color: COLORS.primary,
            marginBottom: 4
          }}>
            +{calculatePoints()} stars
            <span
              data-testid="log-activity-streak-qualifier"
              style={{ fontSize: 12, fontWeight: 400, color: COLORS.textMuted, marginLeft: 6 }}
            >
              (plus any daily streak bonus)
            </span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {(() => {
              // Mirror calculatePoints()'s mode multiplier map so the
              // caption always reflects the real mode and multiplier.
              const modeMultiplier = { individual: 1, team: 3, company: 3, community: 5 };
              const mode = selectedChallenge?.mode || 'individual';
              const mult = modeMultiplier[mode] || 1;
              if (mode === 'individual') return 'Based on activity logged';
              return `Includes ${mult}x ${mode} bonus`;
            })()}
          </div>
        </div>


        {/* Optional: Also share to WE Feed. Off by default.
            Rendered BEFORE the Log Progress button so the button lives
            at the very bottom of the form, matching the layout spec. */}
        <div
          data-testid="log-activity-share-toggle-row"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: 10,
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>Also share to WE Feed</span>
            <span style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>Optional. Off by default.</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={shareEnabled}
            data-testid="log-activity-share-toggle"
            onClick={() => {
              setShareEnabled(prev => {
                const next = !prev;
                if (next && !shareContent.trim()) {
                  const { content } = buildLogSharePrefill({
                    challenge: selectedChallenge, logValue, value, selectedMetricIdx,
                  });
                  setShareContent(content);
                }
                return next;
              });
            }}
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: shareEnabled ? COLORS.primary : '#D1D5DB',
              border: 'none', position: 'relative', cursor: 'pointer', flexShrink: 0,
              transition: 'background 0.2s',
            }}
          >
            <span style={{
              position: 'absolute', top: 2, left: shareEnabled ? 22 : 2, width: 20, height: 20,
              borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }} />
          </button>
        </div>

        {shareEnabled && (
          <div
            data-testid="log-activity-share-section"
            style={{
              border: `1px solid ${COLORS.border}`, borderRadius: 10,
              padding: 14, marginBottom: 16, background: COLORS.white,
            }}
          >
            <textarea
              data-testid="log-activity-share-content"
              value={shareContent}
              onChange={(e) => setShareContent(e.target.value)}
              placeholder="What did you do? Share it with your team…"
              rows={4}
              style={{
                width: '100%', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
                boxSizing: 'border-box', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {SHARE_TAG_SUGGESTIONS.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => insertShareTag(tag)}
                  style={{
                    padding: '4px 10px', background: COLORS.softBg, border: `1px solid ${COLORS.border}`,
                    borderRadius: 999, fontSize: 12, color: COLORS.textSecondary, cursor: 'pointer',
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <ImageUploadPicker
                testId="log-activity-share-image-btn"
                label={shareMediaUrl || sharePreviewUrl ? 'Change Image' : 'Add Image'}
                onUploadStart={(dataUrl) => { setSharePreviewUrl(dataUrl); setShareUploading(true); }}
                onUploadComplete={(url) => { setShareMediaUrl(url); setSharePreviewUrl(''); setShareUploading(false); }}
                onUploadError={() => { setSharePreviewUrl(''); setShareUploading(false); }}
              />
              {(sharePreviewUrl || shareMediaUrl) && (
                <img
                  src={sharePreviewUrl || shareMediaUrl}
                  alt="Share preview"
                  style={{ display: 'block', width: '100%', maxHeight: 200, objectFit: 'contain', background: '#F3F4F6', borderRadius: 8, marginTop: 10 }}
                />
              )}
              {shareUploading && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6 }}>
                  Uploading image…
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button — pinned as the LAST element in the form. */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit || shareUploading}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: canSubmit ? COLORS.primary : (!logStatus.can_log ? '#cccccc' : COLORS.border),
            color: canSubmit ? COLORS.white : '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 500,
            cursor: (submitting || !canSubmit || shareUploading) ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
            marginBottom: 80,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          {submitting && (
            <span
              data-testid="log-activity-submit-spinner"
              aria-hidden="true"
              style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
                display: 'inline-block',
                animation: 'logSpin 0.8s linear infinite',
              }}
            />
          )}
          <span>
            {submitting ? 'Logging…' : (!logStatus.can_log ? logStatus.button_text : 'Log Progress')}
          </span>
        </button>
        <style>{`@keyframes logSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
      
      <BottomNav active="challenges" />
    </div>
  );
};